/**
 * Vista de llamada de timbre (página Teléfono).
 * Cámara WHEP + overlay Atender/Colgar. Video no conoce SIP.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { VideoStreamViewer } from '../../installed/cameras';
import DoorbellCallOverlay from './DoorbellCallOverlay';
import { sipSoftphone } from '../../services/softphone/SipSoftphone';
import {
  reportSoftphoneAnswered,
  reportSoftphoneEnded,
} from '../../services/softphone/softphoneApi';
import { eventBus } from '../../services/EventBus';
import { requestClosePwaFromUserGesture } from '../../utils/softphonePwaExit';
import {
  getDoorbellCameraFromUrl,
  isDoorbellIncomingContext,
  isSoftphoneQuickAnswer,
  peekPendingDoorbellCamera,
} from '../../utils/sessionFlags';

const RING_WAIT_MS = 60_000;

export interface DoorbellCallViewProps {
  cameraId?: number | null;
}

const DoorbellCallView: React.FC<DoorbellCallViewProps> = ({ cameraId: cameraIdProp }) => {
  const [activeCall, setActiveCall] = useState(sipSoftphone.activeCall);
  const [regStatus, setRegStatus] = useState(sipSoftphone.regStatus);
  const [answering, setAnswering] = useState(false);
  const [handoffFailed, setHandoffFailed] = useState(false);
  const answeredReportedRef = useRef(false);
  const [cameraId, setCameraId] = useState<number>(() => {
    if (cameraIdProp && Number.isFinite(cameraIdProp)) return cameraIdProp;
    const pending = peekPendingDoorbellCamera();
    if (pending?.cameraId) return pending.cameraId;
    const fromUrl = getDoorbellCameraFromUrl();
    if (fromUrl != null) return fromUrl;
    return sipSoftphone.cameraId || 23;
  });
  const hangupInFlightRef = useRef(false);
  const [leftIsland, setLeftIsland] = useState(false);
  const claimFailTimerRef = useRef<number | null>(null);

  const liveCall = Boolean(
    activeCall
    && (activeCall.phase === 'ringing' || activeCall.phase === 'active' || activeCall.phase === 'calling'),
  );
  const isDoorbellUi = Boolean(
    liveCall && (
      activeCall!.kind === 'doorbell'
      || isSoftphoneQuickAnswer()
      || isDoorbellIncomingContext()
    ),
  );
  const overlayPhase = leftIsland
    ? 'ended'
    : (isDoorbellUi
      ? activeCall!.phase
      : (handoffFailed
        ? 'failed'
        : (regStatus === 'registered' ? 'ready' : 'waiting')));

  useEffect(() => {
    setActiveCall(sipSoftphone.activeCall);
    setRegStatus(sipSoftphone.regStatus);
    return sipSoftphone.subscribe(() => {
      setActiveCall(sipSoftphone.activeCall);
      setRegStatus(sipSoftphone.regStatus);
      if (sipSoftphone.cameraId && sipSoftphone.cameraId !== cameraId) {
        setCameraId(sipSoftphone.cameraId);
      }
    });
  }, [cameraId]);

  useEffect(() => {
    if (cameraIdProp && Number.isFinite(cameraIdProp) && cameraIdProp !== cameraId) {
      setCameraId(cameraIdProp);
    }
  }, [cameraIdProp, cameraId]);

  // Peek sin claim: no Redirect (el grupo sigue). El ring pulse al celular se mantiene.
  useEffect(() => {
    void sipSoftphone.notifyServerReady();
  }, []);

  // No Redirect al abrir la isla: el ring group tiene que seguir hasta Atender.
  useEffect(() => {
    if (liveCall) {
      setHandoffFailed(false);
      sipSoftphone.stopHandoffRetries();
      if (claimFailTimerRef.current) {
        window.clearTimeout(claimFailTimerRef.current);
        claimFailTimerRef.current = null;
      }
    }
  }, [liveCall]);

  useEffect(() => {
    if (activeCall?.phase === 'active' && answering && activeCall.id && !answeredReportedRef.current) {
      answeredReportedRef.current = true;
      void reportSoftphoneAnswered(activeCall.id).catch(() => { /* ignore */ });
    }
    if (activeCall?.phase === 'active' || activeCall?.phase === 'ended' || !activeCall) {
      if (activeCall?.phase === 'active' || activeCall?.phase === 'ended') {
        setAnswering(false);
      }
    }
  }, [activeCall, answering]);

  useEffect(() => () => {
    if (claimFailTimerRef.current) {
      window.clearTimeout(claimFailTimerRef.current);
      claimFailTimerRef.current = null;
    }
  }, []);

  const handleAnswer = useCallback(() => {
    if (answering || overlayPhase === 'failed' || overlayPhase === 'waiting') return;
    if (overlayPhase !== 'ringing' && overlayPhase !== 'ready') return;
    setAnswering(true);
    sipSoftphone.queueAccept();
    const ac = sipSoftphone.activeCall;
    if (ac?.phase === 'ringing') {
      void sipSoftphone.accept().catch(() => {
        setAnswering(false);
      });
      return;
    }
    sipSoftphone.startHandoffRetries({ intervalMs: 400, maxMs: RING_WAIT_MS });
    if (claimFailTimerRef.current) {
      window.clearTimeout(claimFailTimerRef.current);
    }
    claimFailTimerRef.current = window.setTimeout(() => {
      const cur = sipSoftphone.activeCall;
      if (cur && (cur.phase === 'ringing' || cur.phase === 'active' || cur.phase === 'calling')) {
        return;
      }
      setHandoffFailed(true);
      setAnswering(false);
      sipSoftphone.stopHandoffRetries();
      sipSoftphone.clearPendingAccept();
      sipSoftphone.silenceIncomingRing();
    }, RING_WAIT_MS);
  }, [answering, overlayPhase]);

  const handleHangup = useCallback(() => {
    if (hangupInFlightRef.current) return;
    hangupInFlightRef.current = true;
    setAnswering(false);
    setLeftIsland(true);
    if (claimFailTimerRef.current) {
      window.clearTimeout(claimFailTimerRef.current);
      claimFailTimerRef.current = null;
    }
    sipSoftphone.clearPendingAccept();
    sipSoftphone.stopHandoffRetries();
    sipSoftphone.silenceIncomingRing();
    const quick = isSoftphoneQuickAnswer();
    const wasActive = sipSoftphone.activeCall?.phase === 'active';
    if (quick) {
      requestClosePwaFromUserGesture();
    }
    // Un toque: salir de la isla ya (logueado → pantalla anterior; quick → login).
    try {
      eventBus.emit('softphone:doorbell-hangup');
    } catch { /* ignore */ }
    void sipSoftphone.hangup().then((snap) => {
      // Si no se atendió, no avisar "ended/rejected": el resto tiene que seguir sonando.
      if (snap?.id && wasActive) {
        void reportSoftphoneEnded(snap.id, {
          state: 'ended',
          peer: snap.peer,
          direction: snap.direction,
          kind: 'doorbell',
          duration_sec: Math.max(0, Math.round((Date.now() - snap.startedAt) / 1000)),
        }).catch(() => { /* ignore */ });
      }
    }).finally(() => {
      hangupInFlightRef.current = false;
    });
  }, []);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundColor: '#000',
        zIndex: 20,
        overflow: 'hidden',
      }}
    >
      <VideoStreamViewer
        cameraId={cameraId}
        cameraName="Timbre"
        audioEnabled={false}
        showBandwidth={false}
        connectWhileHidden
      />
      <DoorbellCallOverlay
        visible
        peer={activeCall?.peer}
        phase={overlayPhase}
        muted={!!activeCall?.muted}
        speaker={!!activeCall?.speaker}
        answering={answering}
        onAnswer={overlayPhase === 'failed' ? undefined : handleAnswer}
        onHangup={handleHangup}
        onToggleMute={isDoorbellUi ? () => {
          void sipSoftphone.setMute(!activeCall?.muted);
        } : undefined}
        onToggleSpeaker={isDoorbellUi ? () => {
          void sipSoftphone.setSpeaker(!activeCall?.speaker);
        } : undefined}
      />
    </Box>
  );
};

export default DoorbellCallView;
